import { BarChart3, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useDeleteLesson, useLessons } from '@/hooks/use-lessons'
import { useTenantPath } from '@/lib/tenant'

export default function TeacherLessonsPage() {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useLessons({ search: search || undefined })
  const deleteLesson = useDeleteLesson()
  const tp = useTenantPath()

  const lessons = data?.data ?? []

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`「${title}」を削除しますか？`)) return
    try {
      await deleteLesson.mutateAsync(id)
      toast.success('レッスンを削除しました')
    } catch {
      toast.error('削除に失敗しました')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">レッスン管理</h1>
        <Link to={tp('/teacher/lessons/new')}>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            新規作成
          </Button>
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="レッスンを検索..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={`lesson-skeleton-${i}`} className="h-16" />
          ))}
        </div>
      ) : lessons.length === 0 ? (
        <p className="text-muted-foreground">レッスンがありません。</p>
      ) : (
        <div className="space-y-3">
          {lessons.map((lesson: any) => (
            <Card key={lesson.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base">{lesson.title}</CardTitle>
                  {lesson.hasVideo && <Badge variant="outline">動画あり</Badge>}
                </div>
                <div className="flex gap-2">
                  <Link to={tp(`/teacher/lessons/${lesson.id}/analytics`)}>
                    <Button variant="outline" size="sm" title="分析">
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to={tp(`/teacher/lessons/${lesson.id}/edit`)}>
                    <Button variant="outline" size="sm">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(lesson.id, lesson.title)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
